// Simple API service layer for frontend

// API base must come from REACT_APP_API_BASE in production builds (set at build time in CI/hosting).
// Development falls back to localhost when unset.
function getApiBaseUrl() {
  if (process.env.REACT_APP_API_BASE) {
    return process.env.REACT_APP_API_BASE.replace(/\/$/, '');
  }

  if (process.env.NODE_ENV === 'production') {
    throw new Error(
      'REACT_APP_API_BASE must be set at build time for production. See ENVIRONMENT_VARIABLES_CHECKLIST.md'
    );
  }

  return 'http://localhost:5050/api';
}

export const DEFAULT_BASE_URL = getApiBaseUrl();


function getBaseUrl() {
  return DEFAULT_BASE_URL.replace(/\/$/, '');
}

function getAuthHeaders() {
  const token = localStorage.getItem('ifm_admin_token');
  const headers = { 
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

async function handleResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  let data;
  
  try {
    data = isJson ? await response.json().catch(() => ({})) : await response.text();
  } catch (parseError) {
    console.error('Failed to parse response:', parseError);
    throw new Error('Failed to parse server response');
  }
  
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    if (isJson && data) {
      message = data.error || data.message || message;
    } else if (typeof data === 'string') {
      message = data;
    }
    const error = new Error(message);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

// Wrapper for fetch with network error handling
async function safeFetch(url, options = {}) {
  try {
    // Create timeout controller for older browsers
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
    
    const response = await fetch(url, {
      ...options,
      signal: options.signal || controller.signal
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    // Handle network errors, timeouts, and other fetch failures
    if (error.name === 'AbortError' || error.name === 'TimeoutError') {
      throw new Error('Request timeout - please check your internet connection and try again');
    } else if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
      throw new Error('Network error - unable to reach server. Please check your internet connection.');
    } else if (error.message) {
      throw error;
    } else {
      throw new Error('An unexpected error occurred. Please try again.');
    }
  }
}

// Auth
export async function adminLogin({ username, password }) {
  const url = `${getBaseUrl()}/admin/login`;
  if (process.env.NODE_ENV === 'development') {
    console.log('Admin login request to:', url);
  }
  
  const res = await safeFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
    credentials: 'include' // Include credentials for CORS
  });
  
  const data = await handleResponse(res);
  if (data && data.token) {
    localStorage.setItem('ifm_admin_token', data.token);
    localStorage.setItem('ifm_admin_profile', JSON.stringify(data.admin || {}));
  }
  return data;
}

export async function adminVerify() {
  const url = `${getBaseUrl()}/admin/verify`;
  if (process.env.NODE_ENV === 'development') {
    console.log('Admin verify request to:', url);
  }
  
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  
  if (!res.ok) {
    console.error('Admin verify failed:', res.status, res.statusText);
  }
  
  return handleResponse(res);
}

export function adminLogout() {
  localStorage.removeItem('ifm_admin_token');
  localStorage.removeItem('ifm_admin_profile');
}

// News (Blogs)
export async function listNews(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  const url = `${getBaseUrl()}/news${queryString ? `?${queryString}` : ''}`;
  const res = await safeFetch(url, {
    credentials: 'include' // Include credentials for CORS
  });
  const data = await handleResponse(res);
  // Handle both old format (array) and new format (object with news array)
  if (Array.isArray(data)) {
    return data;
  }
  // If paginated response, extract the news array
  if (data && data.news && Array.isArray(data.news)) {
    return data.news; // Return just the array for frontend pages
  }
  return [];
}

export async function createNews(payload) {
  const isFormData = payload instanceof FormData;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(`${getBaseUrl()}/news`, {
    method: 'POST',
    headers: isFormData
      ? {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
      : getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: isFormData ? payload : JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function updateNews(id, payload) {
  const isFormData = payload instanceof FormData;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(`${getBaseUrl()}/news/${id}`, {
    method: 'PUT',
    headers: isFormData
      ? {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
      : getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: isFormData ? payload : JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function deleteNews(id) {
  const res = await safeFetch(`${getBaseUrl()}/news/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

// Events
export async function listEvents(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  const url = `${getBaseUrl()}/events${queryString ? `?${queryString}` : ''}`;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include' // Include credentials for CORS
  });
  const data = await handleResponse(res);
  // Handle both old format (array) and new format (object with events array)
  if (Array.isArray(data)) {
    return data;
  }
  // If paginated response, extract the events array
  if (data && data.events && Array.isArray(data.events)) {
    return data.events; // Return just the array for frontend pages
  }
  return [];
}

export async function listEventsWithMeta(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  const url = `${getBaseUrl()}/events${queryString ? `?${queryString}` : ''}`;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include'
  });
  const data = await handleResponse(res);
  if (Array.isArray(data)) {
    return {
      events: data,
      total: data.length,
      page: 1,
      totalPages: 1
    };
  }
  if (data && data.events && Array.isArray(data.events)) {
    return data;
  }
  return {
    events: [],
    total: 0,
    page: 1,
    totalPages: 0
  };
}

/**
 * Load all event pages (public: published only; admin token: includes drafts).
 * Uses REACT_APP_EVENTS_PAGE_LIMIT (default 100, max 200) and REACT_APP_EVENTS_MAX_PAGES (default 30, max 100).
 */
export async function fetchAllEventsPages(extraFilters = {}) {
  const pageLimitRaw = process.env.REACT_APP_EVENTS_PAGE_LIMIT;
  const pageLimit = Math.min(
    Math.max(parseInt(pageLimitRaw !== undefined && pageLimitRaw !== '' ? pageLimitRaw : '100', 10) || 100, 10),
    200
  );
  const maxPagesRaw = process.env.REACT_APP_EVENTS_MAX_PAGES;
  const maxPages = Math.min(
    Math.max(parseInt(maxPagesRaw !== undefined && maxPagesRaw !== '' ? maxPagesRaw : '30', 10) || 30, 1),
    100
  );

  const all = [];
  let page = 1;

  while (page <= maxPages) {
    const meta = await listEventsWithMeta({ ...extraFilters, page, limit: pageLimit });
    const batch = Array.isArray(meta.events) ? meta.events : [];
    all.push(...batch);
    const tp = Number(meta.totalPages);
    const hasValidTp = Number.isFinite(tp) && tp >= 1;
    if (hasValidTp) {
      if (page >= tp) break;
    } else if (batch.length < pageLimit) {
      break;
    }
    page += 1;
  }

  return all;
}

export async function createEvent(payload) {
  const isFormData = payload instanceof FormData;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(`${getBaseUrl()}/events`, {
    method: 'POST',
    headers: isFormData
      ? {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
      : getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: isFormData ? payload : JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function updateEvent(id, payload) {
  const isFormData = payload instanceof FormData;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(`${getBaseUrl()}/events/${id}`, {
    method: 'PUT',
    headers: isFormData
      ? {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
      : getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: isFormData ? payload : JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function getEvent(id) {
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(`${getBaseUrl()}/events/${id}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function deleteEvent(id) {
  const res = await safeFetch(`${getBaseUrl()}/events/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

// Event Registrations
export async function createEventRegistration(payload) {
  const res = await safeFetch(`${getBaseUrl()}/event-registrations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function listEventRegistrations(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  const url = `${getBaseUrl()}/event-registrations${queryString ? `?${queryString}` : ''}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

export async function getEventRegistration(id) {
  const res = await safeFetch(`${getBaseUrl()}/event-registrations/${id}`, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

export async function updateEventRegistrationStatus(id, status, notes) {
  const res = await safeFetch(`${getBaseUrl()}/event-registrations/${id}/status`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify({ status, notes })
  });
  return handleResponse(res);
}

export async function cancelEventRegistration(id, cancellationReason) {
  const res = await safeFetch(`${getBaseUrl()}/event-registrations/${id}/cancel`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify({ cancellationReason })
  });
  return handleResponse(res);
}

export async function getEventRegistrationsByEvent(eventId) {
  const res = await safeFetch(`${getBaseUrl()}/event-registrations/event/${eventId}`, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

// Gallery Photos/Videos
export async function listPhotos() {
  const res = await safeFetch(`${getBaseUrl()}/gallery/photos`, {
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

export async function listVideos() {
  const res = await safeFetch(`${getBaseUrl()}/gallery/videos`, {
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

export async function createPhoto(payload) {
  // Check if payload is FormData (file upload) or regular object (URL)
  const isFormData = payload instanceof FormData;
  const headers = isFormData ? {} : getAuthHeaders();
  
  const res = await safeFetch(`${getBaseUrl()}/gallery/photos`, {
    method: 'POST',
    headers: {
      ...headers,
      // Don't set Content-Type for FormData, browser will set it with boundary
      ...(isFormData ? {} : { 'Content-Type': 'application/json' })
    },
    credentials: 'include', // Include credentials for CORS
    body: isFormData ? payload : JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function createVideo(payload) {
  const res = await safeFetch(`${getBaseUrl()}/gallery/videos`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function deletePhoto(id) {
  const res = await safeFetch(`${getBaseUrl()}/gallery/photos/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

export async function deleteVideo(id) {
  const res = await safeFetch(`${getBaseUrl()}/gallery/videos/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

// Members
export async function listMembers(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  const url = `${getBaseUrl()}/members${queryString ? `?${queryString}` : ''}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  const data = await handleResponse(res);
  // Handle both old format (array) and new format (object with members array)
  if (Array.isArray(data)) {
    return data;
  }
  // Return the full response object if it has pagination info, otherwise just members array
  if (data.members && (data.total !== undefined || data.page !== undefined)) {
    return data; // Return full object with pagination
  }
  return data.members || [];
}

export async function getMember(id) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}`, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

/** Public, rate-limited: worker count / plan ids for renewal registration prefill only */
export async function getRenewalPrefill(memberMongoId) {
  const res = await safeFetch(`${getBaseUrl()}/members/renewal-prefill/${encodeURIComponent(memberMongoId)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function createMember(payload) {
  const res = await safeFetch(`${getBaseUrl()}/members`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function updateMember(id, payload) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function deleteMember(id) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

export async function lookupMember(identifier) {
  const res = await safeFetch(`${getBaseUrl()}/members/lookup/${encodeURIComponent(identifier)}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

/** Admin-only: extends membership expiry (requires admin JWT) */
export async function renewMember(id) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}/renew`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({})
  });
  return handleResponse(res);
}

export async function approveMember(id, approvedBy, markPaymentCompleted) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}/approve`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify({ 
      approvedBy: approvedBy || 'Admin',
      markPaymentCompleted: markPaymentCompleted || false
    })
  });
  return handleResponse(res);
}

export async function activateMember(id) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}/activate`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function deactivateMember(id, reason) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}/deactivate`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ reason })
  });
  return handleResponse(res);
}

export async function rejectMember(id, rejectionReason, approvedBy) {
  const res = await safeFetch(`${getBaseUrl()}/members/${id}/reject`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify({ 
      rejectionReason: rejectionReason || 'Your membership application has been reviewed and unfortunately could not be approved at this time.',
      approvedBy: approvedBy || 'Admin'
    })
  });
  return handleResponse(res);
}

// Membership Plans
export async function listMembershipPlans() {
  const res = await safeFetch(`${getBaseUrl()}/membership-plans`, {
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

// Calculate membership fee based on worker count
export async function calculateMembershipFee(workerCount) {
  const res = await safeFetch(`${getBaseUrl()}/membership-plans/calculate-fee`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ workerCount })
  });
  return handleResponse(res);
}

// Get unified plan details with training info
export async function getUnifiedPlanDetails() {
  const res = await safeFetch(`${getBaseUrl()}/membership-plans/stewardship/details`, {
    credentials: 'include'
  });
  return handleResponse(res);
}

// Contact submissions
export async function submitContactForm(payload) {
  const res = await safeFetch(`${getBaseUrl()}/contact/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function listContactSubmissions(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  const url = `${getBaseUrl()}/contact/submissions${queryString ? `?${queryString}` : ''}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  const data = await handleResponse(res);
  // Handle both old format (array) and new format (object with submissions array)
  if (Array.isArray(data)) {
    return data;
  }
  if (data.submissions && (data.total !== undefined || data.page !== undefined)) {
    return data; // Return full object with pagination
  }
  return data.submissions || [];
}

export async function updateContactSubmission(id, payload) {
  const res = await safeFetch(`${getBaseUrl()}/contact/submissions/${id}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

// Payments
export async function updatePaymentStatus(paymentId, status, failureReason, paymentMethod) {
  const res = await safeFetch(`${getBaseUrl()}/payments/${paymentId}/status`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify({ status, failureReason, paymentMethod })
  });
  return handleResponse(res);
}

export async function createCashPayment(payload) {
  const res = await safeFetch(`${getBaseUrl()}/payments/cash`, {
    method: 'POST',
    headers: getAuthHeaders(),
    credentials: 'include',
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function getPayment(id) {
  const res = await safeFetch(`${getBaseUrl()}/payments/${id}`, {
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

// Grievances
export async function createGrievance(payload) {
  const res = await safeFetch(`${getBaseUrl()}/grievances`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function listGrievances(filters = {}) {
  const queryParams = new URLSearchParams();
  Object.keys(filters).forEach(key => {
    if (filters[key]) {
      queryParams.append(key, filters[key]);
    }
  });
  const queryString = queryParams.toString();
  const url = `${getBaseUrl()}/grievances${queryString ? `?${queryString}` : ''}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  const data = await handleResponse(res);
  // Handle both old format (array) and new format (object with grievances array)
  if (Array.isArray(data)) {
    return data;
  }
  if (data.grievances && (data.total !== undefined || data.page !== undefined)) {
    return data; // Return full object with pagination
  }
  return data.grievances || [];
}

export async function updateGrievance(id, payload) {
  const res = await safeFetch(`${getBaseUrl()}/grievances/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function updateGrievanceStatus(id, status) {
  const res = await safeFetch(`${getBaseUrl()}/grievances/${id}/status`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    credentials: 'include', // Include credentials for CORS
    body: JSON.stringify({ status })
  });
  return handleResponse(res);
}

// Admin Dashboard Statistics
export async function getDashboardStats() {
  const res = await safeFetch(`${getBaseUrl()}/admin/dashboard/stats`, {
    headers: getAuthHeaders(),
    credentials: 'include' // Include credentials for CORS
  });
  return handleResponse(res);
}

// Audit Logs
export async function listAuditLogs(queryString = '') {
  const url = `${getBaseUrl()}/audit-logs${queryString}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function getAuditLogStats(queryString = '') {
  const url = `${getBaseUrl()}/audit-logs/stats${queryString}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function getAuditLogsByEntity(entityType, entityId, queryString = '') {
  const url = `${getBaseUrl()}/audit-logs/entity/${entityType}/${entityId}${queryString}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function getAuditLogsByAdmin(adminId, queryString = '') {
  const url = `${getBaseUrl()}/audit-logs/admin/${adminId}${queryString}`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

// Services
export async function listServices() {
  const url = `${getBaseUrl()}/services`;
  const res = await safeFetch(url, {
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function getService(id) {
  const url = `${getBaseUrl()}/services/${id}`;
  const res = await safeFetch(url, {
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function createService(payload) {
  const url = `${getBaseUrl()}/services`;
  const res = await safeFetch(url, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function updateService(id, payload) {
  const url = `${getBaseUrl()}/services/${id}`;
  const res = await safeFetch(url, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function deleteService(id) {
  const url = `${getBaseUrl()}/services/${id}`;
  const res = await safeFetch(url, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

// Companies
export async function listCompanies() {
  const url = `${getBaseUrl()}/companies`;
  const res = await safeFetch(url, {
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function listCompaniesAdmin() {
  const url = `${getBaseUrl()}/companies/admin`;
  const res = await safeFetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

export async function createCompany(payload) {
  const isFormData = payload instanceof FormData;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(`${getBaseUrl()}/companies`, {
    method: 'POST',
    headers: isFormData
      ? {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
      : getAuthHeaders(),
    credentials: 'include',
    body: isFormData ? payload : JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function updateCompany(id, payload) {
  const isFormData = payload instanceof FormData;
  const token = localStorage.getItem('ifm_admin_token');
  const res = await safeFetch(`${getBaseUrl()}/companies/${id}`, {
    method: 'PUT',
    headers: isFormData
      ? {
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
      : getAuthHeaders(),
    credentials: 'include',
    body: isFormData ? payload : JSON.stringify(payload)
  });
  return handleResponse(res);
}

export async function deleteCompany(id) {
  const res = await safeFetch(`${getBaseUrl()}/companies/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
    credentials: 'include'
  });
  return handleResponse(res);
}

const api = {
  adminLogin,
  adminVerify,
  adminLogout,
  listNews,
  createNews,
  updateNews,
  deleteNews,
  listEvents,
  getEvent,
  createEvent,
  updateEvent,
  deleteEvent,
  listPhotos,
  listVideos,
  createPhoto,
  createVideo,
  deletePhoto,
  deleteVideo,
  listMembers,
  createMember,
  updateMember,
  deleteMember,
  listMembershipPlans,
  submitContactForm,
  listContactSubmissions,
  updateContactSubmission,
  createGrievance,
  listGrievances,
  updateGrievance,
  updateGrievanceStatus,
  listAuditLogs,
  getAuditLogStats,
  getAuditLogsByEntity,
  getAuditLogsByAdmin,
  listCompanies,
  listCompaniesAdmin,
  createCompany,
  updateCompany,
  deleteCompany
};

export default api;



